/**
 * Componente principal da janela de detalhes do pedido
 * 
 * Decisões de UI/UX:
 * - Layout otimizado para tamanho mínimo de 285px 
 * - Design responsivo que se adapta a diferentes larguras
 * - Botões e ações com espaçamento adequado
 * - Scroll inteligente para conteúdo extenso
 * 
 * FLUXO SIMPLIFICADO PARA PRODUÇÃO:
 * - Apenas abrir arquivo + monitoramento
 * - Sem reorganização de objetos
 * - Sem layerOrganizador
 * - Foco em exportação
 */

import { useState, useRef, useEffect, useCallback } from 'react';
import { MonitorStatusPanel } from './components/MonitorStatusPanel';
import { usePermissions } from '../../contexts/PermissionContext';
import { useOrders } from '../../contexts/OrderContext';
import { roleColors } from '../../types/permissions';

import {
  HeaderSection,
  FooterSection,
  DebugModal,
  LoadingOverlay,
  LayoutPromptModal,
  VirtualizedObjectList,
  useObjectManagement,
  useContractionLogic,
  useMetricsCalculation,
  useRejectionReasons,
  formatValue
} from './';

import { ChecklistWindow } from '../ChecklistWindow';
import { ExportLayoutProgressModal } from '../ExportLayout/ExportLayoutProgressModal';
import { ExportTypeSelector } from '../ExportModal/components/ExportTypeSelector';
import type { ExportType } from '../ExportModal/types';
import { layerOrganizador } from '../../lib/corelOperations';
import { useMonitoramentoObjetos } from './hooks/useMonitoramentoObjetos';
import { useModalStates } from './hooks/useModalStates';
import { useSnapshotHydration } from './hooks/useSnapshotHydration';
import { useOrderDetailsReset } from './hooks/useOrderDetailsReset';
import { useOrderActions } from './hooks/useOrderActions';
import { useSavePrevalidation } from './hooks/useSavePrevalidation';
import { sendToTerminal, logger } from '../../lib/terminalLogger';
import { notify } from '../../lib/globalNotify';
import { storage } from '../../lib/storage/storage';
import RefuseModal from './components/RefuseModal';
import { OrcDetailsModal } from '../PedidosPage/components/shared/OrcDetailsModal';
import { useAdvancedValidations } from './hooks/useAdvancedValidations';
import { resolveValidationMode } from './utils/validationMode';
import { useRefuseAction } from './hooks/useRefuseAction';
import { useOrderUtilityHandlers } from './hooks/useOrderUtilityHandlers';
import { useProductionOpenFlow } from './hooks/useProductionOpenFlow';
import { useProductionExportAction } from './hooks/useProductionExportAction';
import { monitoradorObjetos } from '../../lib/corel/MonitoradorObjetos';
import { getProductionLinkageContext } from '../../lib/productionLinkage';

interface OrderDetailsWindowProps {
  onClose: () => void;
  onNavigateToMain?: () => void;
  onNavigateToExport?: () => void; // Nova callback para navegar para página de exportação
  onExportPrepareChange?: (state: { preparing: boolean; step: 'sync' | 'prepare'; error: string | null }) => void; // NOVO
  pedidoId?: string;
  itemId?: string;
  empresa?: string;
  initialDelivery?: string;
  normalizeDelivery?: (value: string) => string;
  checklistMode?: 'default' | 'temp';
  checklistTempPathBuilder?: (saveData: any) => { filePath: string | null; fileName: string | null } | undefined;
  // Hidratar objetos a partir do snapshot do pipeline preparado (usado no Save Temp)
  hydrateFromPreparedSnapshot?: boolean;
  expectedOpenSessionId?: string;
  // ✅ UI/UX: Callback quando snapshot é aplicado (permite parent controlar overlay único)
  onSnapshotReady?: () => void;
  // ✅ UI/UX: Esconde LoadingOverlay interno (quando parent já mostra overlay)
  hideInternalLoading?: boolean;
}

export default function OrderDetailsWindow({ onClose, onNavigateToMain, onNavigateToExport, onExportPrepareChange, pedidoId, itemId, initialDelivery, normalizeDelivery, checklistMode = 'default', checklistTempPathBuilder, hydrateFromPreparedSnapshot, expectedOpenSessionId, onSnapshotReady, hideInternalLoading }: OrderDetailsWindowProps) {
  // ⚠️ Hooks devem ser chamados sempre na mesma ordem (não podem estar em try/catch)
  const { role, userInfo } = usePermissions();
  const { selectedPedido } = useOrders();
  const colors = roleColors[role];
  const isTempSaveFlow = checklistMode === 'temp';
  const actionRole = isTempSaveFlow ? 'vendedor' : role;
  const validationMode = resolveValidationMode({
    isTempSaveFlow,
    isQuotation: pedidoId === 'quotation',
  });

  // ✅ useRef para rastrear se já foi montado
  const hasMountedRef = useRef(false);
  const componentId = useRef(`odw-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`);
  const mountCountRef = useRef(0);

  // 🔍 LOG CRÍTICO: Rastrear mudanças nas props pedidoId e itemId
  const previousPedidoIdRef = useRef<string | undefined>(undefined);
  const previousItemIdRef = useRef<string | undefined>(undefined);

  // ✅ CONTROLE DE PERSISTÊNCIA: Flag para evitar cleanup do storage ao navegar para exportação
  const isNavigatingToExportRef = useRef(false);

  // 🔍 LOG CRÍTICO: Detectar remontagem (dentro de useEffect para evitar side effects)
  useEffect(() => {
    // ✅ Incrementar dentro do useEffect (não no render)
    mountCountRef.current += 1;
    const currentMountCount = mountCountRef.current;
    const isRemount = currentMountCount > 1;

    // ✅ Log reduzido - apenas primeira montagem (não remounts do StrictMode)
    if (!isRemount) {
      sendToTerminal('🎬 [ORDER_DETAILS] Componente montado', {
        pedidoId,
        itemId,
        checklistMode,
        isTempSaveFlow: checklistMode === 'temp',
        actionRole: checklistMode === 'temp' ? 'vendedor' : role,
        hydrateFromPreparedSnapshot,
        hasSelectedPedido: !!selectedPedido,
        selectedPedidoId: selectedPedido?.orc_id,
        hasItens: !!selectedPedido?.itens,
        itensLength: selectedPedido?.itens?.length
      });
    }
  }, []); // ← Vazio = executa apenas na montagem

  useEffect(() => {
    const currentPedidoId = pedidoId;
    const currentItemId = itemId;
    const previousPedidoId = previousPedidoIdRef.current;
    const previousItemId = previousItemIdRef.current;

    if (currentPedidoId !== previousPedidoId || currentItemId !== previousItemId) {
      previousPedidoIdRef.current = currentPedidoId;
      previousItemIdRef.current = currentItemId;
    }
  }, [pedidoId, itemId, selectedPedido?.orc_id]);

  // 🔥 LOG CRÍTICO: Estado inicial do componente (apenas no mount)
  useEffect(() => {
    if (hasMountedRef.current) return;
    hasMountedRef.current = true;

    // ✅ Log removido - já logado no useEffect de montagem acima (reduzir duplicação)

    // ✅ Cleanup para detectar unmount
    return () => {
      // 🔍 LOG CRÍTICO: Stack trace completo do unmount
      const unmountStack = new Error().stack?.split('\n').slice(1, 15) || [];
      const unmountStackInfo = unmountStack
        .map(line => line.trim())
        .filter(line => !line.includes('OrderDetailsWindow.tsx'))
        .slice(0, 8)
        .join(' → ');

      logger.debug(`🧹 [${componentId.current}] OrderDetailsWindow VAI DESMONTAR`, {
        pedidoId,
        itemId,
        unmountStack: unmountStackInfo,
        timestamp: new Date().toISOString()
      });

      // ✅ Log removido - reduzir verbosidade (desmontagem é normal do React StrictMode)
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const [showMonitorStatus, setShowMonitorStatus] = useState(false);
  // Modal "Detalhes do pedido" (reaproveita OrcDetailsModal): mostra cliente,
  // item mãe (camiseta) pareado à impressão, observações, valores e layouts.
  const [showOrderDetails, setShowOrderDetails] = useState(false);

  // Delivery state
  const getNormalizedDeliveryValue = useCallback((value?: string) => {
    const nextValue = value || 'Hoje';
    return normalizeDelivery ? normalizeDelivery(nextValue) : nextValue;
  }, [normalizeDelivery]);
  const [delivery, setDelivery] = useState(() => getNormalizedDeliveryValue(initialDelivery));
  const handleDeliveryChange = useCallback((value: string) => {
    const normalized = getNormalizedDeliveryValue(value);
    setDelivery(normalized);
  }, [getNormalizedDeliveryValue]);

  useEffect(() => {
    setDelivery(getNormalizedDeliveryValue(initialDelivery));
  }, [getNormalizedDeliveryValue, initialDelivery, itemId, pedidoId]);

  // ✅ REF para showChecklist (necessário porque useModalStates é chamado depois de useMonitoramentoObjetos)
  const showChecklistRef = useRef(false);

  // Configurar monitoramento de objetos
  const {
    isMonitorando,
    webViewInicializado,
    estatisticas,
    testarComunicacao,
    notificarWebViewDisponivel,
    inicializarMonitoramento
  } = useMonitoramentoObjetos({
    onObjetoAlterado: () => {
      refreshObjects(); // Atualizar lista de objetos
    },
    onObjetoCriado: () => {
      refreshObjects(); // Atualizar lista de objetos
    },
    onObjetoRemovido: () => {
      refreshObjects(); // Atualizar lista de objetos
    },
    // Suporte a bloco unificado (atualização otimista)
    onObjetoAtualizar: (obj) => {
      try { (applyCorelObjetoAtualizar as any)?.(obj); } catch { }
    },
    onObjetoRemover: (staticId) => {
      try { (applyCorelObjetoRemover as any)?.(staticId); } catch { }
    },
    onDocumentoFechado: () => {
      // ✅ PROTEÇÃO: Não fechar se salvamento em progresso (checklist aberto)
      // Usa ref porque showChecklist ainda não foi definido neste ponto
      if (showChecklistRef.current) {
        sendToTerminal('⏭️ [ORDER_DETAILS] Ignorando documento fechado - salvamento em progresso', {
          pedidoId,
          itemId,
          showChecklist: true
        });
        return;
      }

      sendToTerminal('🚪 [ORDER_DETAILS] Documento fechado pelo usuário', {
        pedidoId,
        itemId
      });

      onClose();

      if (onNavigateToMain) {
        onNavigateToMain();
      }
    },
    getArquivoMonitorado: () => ({
      path: (selectedItem as any)?.arquivo?.diretorio as string | undefined,
      name: (selectedItem as any)?.arquivo?.nome ||
        (selectedItem as any)?.caminho_arquivo?.split(/[\\/]/).pop() ||
        (selectedPedido as any)?.nome_arquivo as string | undefined,
      // 🆕 Adicionar IDs para abort
      orcId: selectedPedido?.orc_id,
      oitId: selectedItem?.oit_id
    }),
    // ✅ CORREÇÃO: Desabilitar auto-init quando a pipeline já preparou snapshot/monitor
    // - Produção usa pipeline dedicada (useProductionOpenFlow)
    // - Quotation save com hydrateFromPreparedSnapshot também já chega preparado
    // Isso evita concorrência entre a pipeline unificada e um novo init local do monitor.
    autoInit: role !== 'producao' && !hydrateFromPreparedSnapshot
  });

  // ✅ CLEANUP AUTOMÁTICO via Sistema de Referência Contada
  // Não é mais necessário parar monitor manualmente aqui.
  // Cada hook que usa monitor (useProductionOpenFlow, useProductionExportAction, etc.)
  // agora gerencia sua própria referência via adicionarReferencia/removerReferencia.
  // O monitor para automaticamente quando todas as referências são removidas.

  // 🔥 VALIDAÇÃO CRÍTICA: Verificar se selectedPedido está disponível
  if (!selectedPedido) {
    sendToTerminal('❌ [ORDER_DETAILS] selectedPedido não disponível', {
      pedidoId,
      itemId
    });
    return null;
  }

  if (!selectedPedido.itens || selectedPedido.itens.length === 0) {
    sendToTerminal('❌ [ORDER_DETAILS] selectedPedido.itens inválido', {
      selectedPedidoId: selectedPedido?.orc_id,
      hasItens: !!selectedPedido.itens,
      itensLength: selectedPedido.itens?.length || 0,
      pedidoId,
      itemId
    });
    return null;
  }

  // Selecionar o item específico baseado no itemId
  const selectedItem = itemId
    ? selectedPedido.itens.find(item => item.oit_id.toString() === itemId)
    : selectedPedido.itens[0]; // Fallback para o primeiro item

  useEffect(() => {
    if (!hasMountedRef.current) return;

    if (!selectedItem) {
      sendToTerminal('❌ [ORDER_DETAILS] selectedItem não encontrado', {
        itemId,
        itensDisponiveis: selectedPedido.itens.map((item: any) => item.oit_id),
        selectedPedidoId: selectedPedido.orc_id
      });
    }
  }, [selectedPedido, selectedItem, pedidoId, itemId]);

  if (!selectedItem) {
    return null;
  }

  // Progresso do item (produção)
  const itemProgresso: any | null = (selectedItem as any)?.progresso || null;
  const itemPendencias: string[] = Array.isArray(itemProgresso?.pendencias) ? itemProgresso.pendencias : [];

  // Verificar a unidade de medida do produto
  const unidadeMedida = selectedItem?.produto?.unidade;
  const isMetros = unidadeMedida?.umd_id === 3; // 3 = Metros, 4 = Unidade

  // Obter as regras se existirem.
  // Regra de negócio importante: alguns fluxos de camiseta (ex.: personalizada/sublimada)
  // podem operar sem entrada correspondente em oneprint.regras_salvamento.
  // Quando isso acontece, o frontend deve apenas consumir a ausência de regras sem
  // inventar fallback específico aqui; o comportamento resultante faz parte do fluxo atual.
  const productRules = selectedItem?.regras;
  const linkageContext = getProductionLinkageContext(selectedItem as any);
  const effectiveProductCategId = linkageContext.finalCategoryId ?? selectedItem?.produto?.categoria;
  const displayMaterialName = linkageContext.finalProductName ?? selectedItem?.produto?.nome ?? 'Material não informado';
  const displayOperationalProductName = linkageContext.isLinkedOperationalItem
    ? linkageContext.operationalProductName
    : null;

  // ✅ Contração default por categoria:
  // - pro_categ_id = 1 (DTF Têxtil) → '2px'
  // - pro_categ_id = 2 (DTF UV) → '1px'
  // - Outros → '0px'
  const defaultContractionByCategory: Record<number, string> = {
    1: '2px', // DTF Têxtil
    2: '1px', // DTF UV
  };
  const categoryId = effectiveProductCategId;
  const defaultContraction = (categoryId && defaultContractionByCategory[Number(categoryId)]) || '0px';

  // Estado para modal de seleção de tipo de exportação
  const [showExportTypeSelector, setShowExportTypeSelector] = useState(false);

  // Estados para modais e painéis (via hook)
  const {
    showExportLayoutModal, setShowExportLayoutModal,
    showLayoutOnlyPrompt, setShowLayoutOnlyPrompt,
    showRefuseModal, setShowRefuseModal,
    showProdPrep, setShowProdPrep,
    prodPrepStep, setProdPrepStep,
    snapshotApplying, setSnapshotApplying,
    showChecklist, setShowChecklist,
    showInfoPanel, setShowInfoPanel,
    resetAllModals
  } = useModalStates();

  // ✅ Atualizar ref quando showChecklist mudar (necessário para onDocumentoFechado funcionar)
  useEffect(() => {
    showChecklistRef.current = showChecklist;
  }, [showChecklist]);

  // Motivos de recusa baseados no produto (via hook)
  const { motivosPrincipais, detalhesSugeridos } = useRejectionReasons({
    proId: selectedItem?.produto?.pro_id,
    produtoNome: selectedItem?.produto?.nome
  });

  // ⚠️ Hooks devem ser chamados sempre na mesma ordem (não podem estar em try/catch)
  // Hooks customizados
  const {
    selectedObjects,
    setSelectedObjects,
    updateObjectQuantity,
    handleView,
    refreshObjects,
    syncAllWithCorel,
    applyCorelObjetoAtualizar,
    applyCorelObjetoRemover
  } = useObjectManagement({
    initialContraction: defaultContraction,
    productRules,
    selectedPedido
  });

  // 🧹 Resetar selectedObjects ao trocar pedido/item (AGORA setSelectedObjects já está disponível)
  const snapshotAppliedRef = useRef<boolean>(false);
  useOrderDetailsReset({ pedidoId, itemId, setSelectedObjects, snapshotAppliedRef });

  // Wrapper para compatibilidade de tipos (Promise<boolean> → Promise<void>)
  const notificarWebViewDisponivelVoid = useCallback(async () => {
    await notificarWebViewDisponivel();
  }, [notificarWebViewDisponivel]);

  // Aplicar snapshot do sync imediatamente ao abrir (AGORA setSelectedObjects e setSnapshotApplying já estão disponíveis)
  useSnapshotHydration({
    hydrateFromPreparedSnapshot,
    pedidoId,
    itemId,
    expectedOpenSessionId,
    selectedPedido,
    setSelectedObjects,
    snapshotAppliedRef,
    setSnapshotApplying,
    isMonitorando,
    inicializarMonitoramento,
    notificarWebViewDisponivel: notificarWebViewDisponivelVoid,
    onSnapshotReady // ✅ UI/UX: Notifica parent quando snapshot está pronto
  });

  const {
    contraction,
    hasMultipleContractions,
    globalContractionValue,
    updateAllContractions,
    handleObjectContractionChange
  } = useContractionLogic({
    selectedObjects,
    setSelectedObjects,
    initialContraction: defaultContraction
  });

  // ✅ Lógica para ocultar contração: pro_categ_id=3 ou pro_categ_id=11 (Sublimação)
  const hiddenContractionCategIds: number[] = [3, 11]; // Sublimação
  const productCategId = effectiveProductCategId;
  const hideByCategId = productCategId && hiddenContractionCategIds.includes(Number(productCategId));
  const showContraction = !hideByCategId;

  // Usar selectedItem.quantidade ao invés de selectedPedido.itens[0].quantidade
  const quantidadeItem = selectedItem?.quantidade ?? selectedPedido?.itens?.[0]?.quantidade ?? 0;

  const {
    totalObjectsWithQuantity,
    totalAreaWithQuantity,
    hasErrors,
    hasExceededMeterage,
    hasSaveErrors,
    metrageExcess,
    compareSoldMeter,
  } = useMetricsCalculation({
    selectedObjects,
    metrosVendidos: quantidadeItem,
    isMetros,
    productRules,
    validationMode,
  });

  const { hasBlocking } = useAdvancedValidations({
    selectedObjects,
    metrosVendidos: quantidadeItem,
    totalAreaWithQuantity,
    isMetros,
    compareSoldMeter,
    produtoNome: selectedItem?.produto?.nome,
  });

  // Guardas de abertura de arquivo (produção) + pipeline via hook
  const [isOpeningFile, setIsOpeningFile] = useState(false);
  const openStartedRef = useRef<boolean>(false);
  const openCompletedRef = useRef<boolean>(false);
  const openRequestIdRef = useRef<string | null>(null);
  const openTimeoutRef = useRef<any>(null);

  useProductionOpenFlow({
    role: actionRole,
    selectedPedido,
    selectedItem,
    resolveCaminhoArquivo: (item: any) => {
      const a = (item as any)?.arquivo;
      const caminho = a?.diretorio || a?.caminho || (item as any)?.caminho_arquivo || null;
      return (typeof caminho === 'string' && caminho.trim().length > 0) ? caminho : null;
    },
    resetAllModals,
    notificarWebViewDisponivel: notificarWebViewDisponivelVoid,
    setShowProdPrep,
    setProdPrepStep,
    setSnapshotApplying,
    isOpeningFile,
    setIsOpeningFile,
    openStartedRef,
    openCompletedRef,
    openRequestIdRef,
    openTimeoutRef,
    storage,
    onClose, // ✅ NOVO: Fechar janela em caso de erro crítico no sync
  });

  // Fluxo padrão (não-produção): manter comportamento original
  useEffect(() => {
    if (role === 'producao') return;
    // ✅ CORREÇÃO: Não organizar se pipeline já preparou (evita chamada redundante)
    if (hydrateFromPreparedSnapshot) {
      sendToTerminal('⏭️ [ORDER_DETAILS] Skip layerOrganizador - pipeline já organizou as camadas');
      return;
    }
    if (webViewInicializado) {
      const requestId = layerOrganizador();
      if (requestId) { /* silêncio */ }
    }
  }, [role, webViewInicializado, hydrateFromPreparedSnapshot]);

  // Handlers
  const { handleExportComplete, cleanupExportStorage } = useOrderUtilityHandlers({
    onClose,
    onNavigateToMain,
    monitorParar: async () => { },
    selectedPedido,
    selectedItem,
    role,
    isRefreshing,
    setIsRefreshing,
    isSyncing,
    setIsSyncing,
    syncAllWithCorel,
    layerOrganizador,
  });

  const { submitRecusa } = useRefuseAction({ selectedPedido, selectedItem, setSelectedPedidoFrozen: (() => { }) as any });

  // Hook para exportação de produção (fluxo completo)
  const { startProductionExport } = useProductionExportAction({
    selectedPedido,
    selectedItem,
    selectedObjects,
    itemPendencias,
    onNavigateToExport: (() => {
      // 🔍 DEBUG: Log ao entrar no wrapper
      sendToTerminal('🔗 [ORDER_DETAILS:WRAPPER] onNavigateToExport wrapper CALLED', {
        hasOnNavigateToExportProp: typeof onNavigateToExport === 'function',
        propType: typeof onNavigateToExport,
      });

      if (onNavigateToExport) {
        sendToTerminal('🔗 [ORDER_DETAILS:WRAPPER] Calling App.tsx onNavigateToExport...');
        onNavigateToExport();
        sendToTerminal('✅ [ORDER_DETAILS:WRAPPER] App.tsx onNavigateToExport called');
      } else {
        sendToTerminal('❌ [ORDER_DETAILS] onNavigateToExport não definido (prop is falsy)');
        notify({
          type: 'error',
          title: 'Exportação indisponível',
          message: 'Navegação para exportação não configurada.',
          duration: 8000,
        });
      }
    }),
    setShowExportLayoutModal,
    setShowLayoutOnlyPrompt,
    resolveCaminhoArquivo: (item: any) => {
      const a = (item as any)?.arquivo;
      const caminho = a?.diretorio || a?.caminho || (item as any)?.caminho_arquivo || null;
      return (typeof caminho === 'string' && caminho.trim().length > 0) ? caminho : null;
    },
    monitoradorObjetos, // NOVO
    onExportPrepareChange, // NOVO
  });

  useEffect(() => {
    return () => {
      // ✅ PROTEÇÃO: Só limpar storage se NÃO estiver navegando para exportação
      // Isso corrige o bug onde os dados eram apagados antes da página de exportação carregar
      if (!isNavigatingToExportRef.current) {
        try { cleanupExportStorage(); } catch { }
      } else {
        sendToTerminal('⏭️ [ORDER_DETAILS] Cleanup de storage pulado (navegando para exportação)');
        // Resetar flag para próximas interações (opcional, pois componente desmonta)
        isNavigatingToExportRef.current = false;
      }
    };
  }, [cleanupExportStorage]);

  const saveValidationEnabled =
    !isTempSaveFlow &&
    !snapshotApplying &&
    !showProdPrep &&
    !isOpeningFile &&
    !!selectedPedido?.orc_id &&
    !!selectedItem?.oit_id;

  const { saveValidationState, saveValidationMessage } = useSavePrevalidation({
    selectedPedido,
    selectedItem,
    selectedObjects,
    enabled: saveValidationEnabled,
    bypassOfficialValidation: isTempSaveFlow,
  });

  // ✅ Ações unificadas via hook (handleAction, handleFinalSave, toggleMonitorStatus)
  const { handleAction, handleFinalSave, toggleMonitorStatus } = useOrderActions({
    role: actionRole,
    selectedPedido,
    selectedItem,
    itemId,
    productRules,
    contraction,
    hasMultipleContractions,
    delivery,
    selectedObjects,
    totalObjectsWithQuantity,
    totalAreaWithQuantity,
    saveValidationState,
    saveValidationMessage,
    hasSaveErrors,
    userInfo,
    setShowChecklist,
    setShowMonitorStatus,
    showMonitorStatus,
    startProductionExport,
    setShowExportTypeSelector,
    openOnlyFlow: linkageContext.isOpenOnlyFlow,
    onClose
  });

  const userInfoMissing = !userInfo;

  // Renderização unificada para todos os roles
  return (
    <div className="fixed inset-0 bg-navy-900 flex flex-col min-w-[285px] w-full max-w-full z-50">
      {/* ✅ UI/UX: Esconde LoadingOverlay interno quando parent controla overlay único */}
      {!hideInternalLoading && (
        <LoadingOverlay
          show={snapshotApplying || showProdPrep || userInfoMissing}
          currentStep={prodPrepStep}
          isLoadingUser={userInfoMissing}
        />
      )}

      {/* Painel de monitoramento no topo */}
      {showMonitorStatus && (
        <MonitorStatusPanel
          isMonitorando={isMonitorando}
          webViewInicializado={webViewInicializado}
          estatisticas={estatisticas}
          onClose={() => setShowMonitorStatus(false)}
          onTestarComunicacao={testarComunicacao}
          onNotificarWebView={notificarWebViewDisponivel}
        />
      )}

      <HeaderSection
        onClose={onClose}
        pedidoId={selectedPedido?.orc_id ?? ''}
        itemId={selectedItem?.oit_id ?? ''}
        nomeFantasia={selectedPedido?.cliente?.nome_fantasia ?? 'Cliente não informado'}
        material={displayMaterialName}
        operationalMaterial={displayOperationalProductName}
        quality={(selectedItem as any)?.qualidade ?? selectedItem?.material?.nome ?? 'Padrão'}
        materialId={selectedItem?.material?.mat_id ?? 0}
        contraction={globalContractionValue}
        delivery={delivery}
        hasMultipleContractions={hasMultipleContractions}
        onContractionChange={updateAllContractions}
        onDeliveryChange={handleDeliveryChange}
        role={actionRole}
        productProId={selectedItem?.produto?.pro_id}
        productCategId={effectiveProductCategId}
        cliente={selectedPedido?.cliente}
        isMonitorando={isMonitorando}
        onToggleMonitorStatus={toggleMonitorStatus}
      />

      {/* Container principal */}
      <div className="flex-1 flex flex-col min-h-0">
        {/* Lista de objetos */}
        <VirtualizedObjectList
          selectedObjects={selectedObjects}
          onQuantityChange={updateObjectQuantity}
          onView={handleView}
          onContractionChange={handleObjectContractionChange}
          onToggleInclude={(id, include) => setSelectedObjects(prev => prev.map(o => o.id === id ? { ...o, include } : o))}
          formatValue={formatValue}
          role={actionRole}
          showContraction={showContraction}
        />
      </div>

      {/* Footer unificado */}
      <FooterSection
        totalObjectsWithQuantity={totalObjectsWithQuantity}
        metrosVendidos={selectedItem.quantidade}
        totalAreaWithQuantity={totalAreaWithQuantity}
        isMetros={isMetros}
        hasExceededMeterage={compareSoldMeter ? hasExceededMeterage : false}
        hasErrors={hasErrors}
        hasSaveErrors={hasSaveErrors}
        hasBlocking={hasBlocking}
        metrageExcess={compareSoldMeter ? metrageExcess : 0}
        saveValidationState={saveValidationState}
        saveValidationMessage={saveValidationMessage}
        role={role}
        actionRole={actionRole}
        openOnlyFlow={linkageContext.isOpenOnlyFlow}
        userName={userInfo?.nome || ''}
        userType={userInfo?.tipo || role}
        roleColors={colors}
        onAction={handleAction}
        onShowDebug={() => setShowInfoPanel(true)}
        onShowOrderDetails={() => setShowOrderDetails(true)}
        onRefuse={!isTempSaveFlow && role === 'producao' ? () => setShowRefuseModal(true) : undefined}
        disableSoldMeter={!compareSoldMeter}
      />

      {/* Modal de exportação de layouts (PNG) */}
      {showExportLayoutModal && (
        <ExportLayoutProgressModal
          onComplete={() => {
            setShowExportLayoutModal(false);
            handleExportComplete();
          }}
        // Sem alvos: hook derivará da ordem atual em currentSaveData
        />
      )}

      {/* Modal crítico */}
      {/* Modal de recusa (produção) */}
      {showRefuseModal && (
        <RefuseModal
          onClose={() => setShowRefuseModal(false)}
          pedidoId={selectedPedido.orc_id}
          itemId={selectedItem.oit_id}
          motivosPrincipais={motivosPrincipais}
          detalhesSugeridos={detalhesSugeridos}
          onSubmit={async ({ motivo, detalhes, observacao, imagens }) => {
            await submitRecusa({ motivo, detalhes, observacao, imagens });
            setShowRefuseModal(false);
          }}
        />
      )}

      {/* Modal de seleção de tipo de exportação (antes de navegar) */}
      {showExportTypeSelector && (
        <ExportTypeSelector
          visible={true}
          onSelect={(type: ExportType) => {
            sendToTerminal('🎯 [ORDER_DETAILS] Tipo de exportação selecionado', { type });

            // ✅ SINALIZAR NAVEGAÇÃO: Evita que o cleanup do useEffect apague os dados do storage
            isNavigatingToExportRef.current = true;

            // 1. Iniciar exportação IMEDIATAMENTE (Fire-and-Forget)
            // Não usamos await aqui para não bloquear a UI esperando a rede
            startProductionExport(type).catch(err => {
              const errorMsg = err instanceof Error ? err.message : String(err);
              sendToTerminal('❌ [ORDER_DETAILS] Erro async na exportação', { error: errorMsg });
              // Se falhar, reverter flag para permitir cleanup futuro
              isNavigatingToExportRef.current = false;
            });

            // 2. Fechar a modal no próximo tick (Defer Execution)
            // Isso evita race conditions e garante que a UI desbloqueie imediatamente
            setTimeout(() => {
              setShowExportTypeSelector(false);
            }, 0);
          }}
          onCancel={() => {
            sendToTerminal('❌ [ORDER_DETAILS] Seleção de tipo cancelada');
            setShowExportTypeSelector(false);
          }}
        />
      )}

      {/* Prompt para apenas Layout quando batch já foi concluído */}
      <LayoutPromptModal
        isOpen={showLayoutOnlyPrompt}
        onClose={() => setShowLayoutOnlyPrompt(false)}
        onLayoutOnly={() => {
          setShowLayoutOnlyPrompt(false);
          setShowExportLayoutModal(true);
        }}
        onFullFlow={() => {
          setShowLayoutOnlyPrompt(false);
          setShowExportLayoutModal(true);
        }}
      />

      {/* Checklist */}
      {showChecklist && (
        <ChecklistWindow
          onSave={handleFinalSave}
          onClose={() => setShowChecklist(false)}
          mode={checklistMode}
          tempPathBuilder={checklistTempPathBuilder ? async (saveData) => checklistTempPathBuilder(saveData) ?? { filePath: null, fileName: null } : undefined}
        />
      )}

      {/* Debug Modal */}
      {showInfoPanel && (
        <DebugModal
          onClose={() => setShowInfoPanel(false)}
          globalContractionValue={globalContractionValue}
          selectedObjects={selectedObjects}
          hasMultipleContractions={hasMultipleContractions}
          productRules={productRules}
          isMetros={isMetros}
        />
      )}

      {/* Detalhes do pedido — reaproveita a OrcDetailsModal da lista.
          Passa o pedido inteiro (itens crus incluem o item mãe/camiseta) + __role
          para habilitar os ícones de status de arquivo no fluxo de produção. */}
      {showOrderDetails && (
        <OrcDetailsModal
          pedido={{ ...selectedPedido, __role: role }}
          item={selectedItem}
          onClose={() => setShowOrderDetails(false)}
        />
      )}
    </div>
  );
}; 
